/**
 * Un numéro de téléphone lisible, pour qui va le composer.
 *
 * Les numéros arrivent comme les familles les ont tapés — `0479/32.38.50`,
 * `0498501004`, `+32 479…`, `479…` sans le zéro. Un numéro belge est remis
 * au format national, groupé comme on le dicte : `0479 32 38 50` pour un
 * GSM (4-2-2-2), `02 123 45 67` ou `010 12 34 56` pour un fixe. Ce qui n'est
 * pas reconnu comme belge — un numéro étranger, deux numéros dans le même
 * champ — reste tel quel : mieux vaut un numéro mal espacé qu'un numéro faux.
 */

export interface TelephoneLisible {
  texte: string;
  /** `tel:+32…`, pour appeler d'un clic ; `null` si le numéro n'est pas sûr. */
  lien: string | null;
}

/** Les zones fixes à un chiffre : Bruxelles, Anvers, Liège, Gand. */
const ZONES_A_UN_CHIFFRE = new Set(['2', '3', '4', '9']);

function national(brut: string): string | null {
  let n = brut.replace(/[^\d+]/g, '');
  if (n.startsWith('00')) n = `+${n.slice(2)}`;
  // « +32 (0)479… » : le zéro entre parenthèses ne se compose pas après +32.
  if (n.startsWith('+32')) return `0${n.slice(3).replace(/^0/, '')}`;
  if (n.startsWith('+')) return null;
  if (/^4\d{8}$/.test(n)) return `0${n}`;
  return n;
}

export function telephoneLisible(brut: string | null | undefined): TelephoneLisible | null {
  const saisi = (brut ?? '').trim();
  if (!saisi) return null;

  const n = national(saisi);
  if (n && /^04\d{8}$/.test(n)) {
    return { texte: `${n.slice(0, 4)} ${n.slice(4, 6)} ${n.slice(6, 8)} ${n.slice(8)}`, lien: `tel:+32${n.slice(1)}` };
  }
  if (n && /^0[1-9]\d{7}$/.test(n)) {
    const texte = ZONES_A_UN_CHIFFRE.has(n[1])
      ? `${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5, 7)} ${n.slice(7)}`
      : `${n.slice(0, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7)}`;
    return { texte, lien: `tel:+32${n.slice(1)}` };
  }

  const international = saisi.replace(/[^\d+]/g, '').replace(/^00/, '+');
  return {
    texte: saisi,
    lien: /^\+\d{8,15}$/.test(international) ? `tel:${international}` : null,
  };
}
